"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogShowcaseConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="229506" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 h-[500px] bg-muted/50 animate-pulse rounded-xl" />
        <div className="lg:col-span-5 flex flex-col gap-8">
          <div className="h-[234px] bg-muted/50 animate-pulse rounded-xl" />
          <div className="h-[234px] bg-muted/50 animate-pulse rounded-xl" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground font-sans">
        <p>Unable to load the latest security reviews at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground font-sans">
        <p>Our testing lab is currently busy. Check back soon for new camera reviews!</p>
      </div>;
  }
  const featuredPost = posts[0];
  const secondaryPosts = posts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {}
      <div className="lg:col-span-7">
        <Card className="h-full overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border-border/60 rounded-2xl group bg-card">
          <div className="relative aspect-video sm:aspect-[16/10] overflow-hidden">
            <img src={featuredPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/smart_camera_hero.png"} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
            {featuredPost.categories && featuredPost.categories.length > 0 && <div className="absolute top-4 left-4 z-10">
                <Badge variant="secondary" className="font-default text-xs uppercase tracking-widest bg-primary text-primary-foreground shadow-md">
                  {featuredPost.categories[0].name}
                </Badge>
              </div>}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
          </div>
          <CardContent className="p-6 md:p-8 flex flex-col gap-4">
            <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground leading-tight group-hover:text-primary transition-colors duration-300">
              <Link to={featuredPost.link || "#"}>{featuredPost.title}</Link>
            </h3>
            <div className="text-muted-foreground font-sans line-clamp-3 text-base leading-relaxed" dangerouslySetInnerHTML={{
            __html: featuredPost.excerpt || ""
          }} />
            <div className="mt-4 pt-2">
              <Link to={featuredPost.link || "#"} className="group/link inline-flex items-center gap-2 text-sm font-bold font-default text-primary uppercase tracking-widest">
                Read Full Review
                <ArrowRight className="w-4 h-4 opacity-0 -translate-x-3 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-300" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="lg:col-span-5 flex flex-col gap-8">
        {secondaryPosts.map((post, i) => <Card key={post.id} data-index={i} className="flex flex-col sm:flex-row overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 border-border/60 rounded-2xl group h-full bg-card">
            <div className="relative sm:w-2/5 aspect-video sm:aspect-auto overflow-hidden shrink-0">
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/security_sensor_detail.png"} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
            </div>
            <CardContent className="p-5 md:p-6 flex flex-col gap-3 justify-center flex-1">
              {post.categories && post.categories.length > 0 && <Badge variant="outline" className="w-fit font-default text-[10px] uppercase tracking-widest text-muted-foreground border-border bg-accent/30">
                  {post.categories[0].name}
                </Badge>}
              <h3 className="text-lg font-bold font-default text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors duration-300">
                <Link to={post.link || "#"}>{post.title}</Link>
              </h3>
              <div className="mt-auto pt-2">
                <Link to={post.link || "#"} className="group/link inline-flex items-center gap-1.5 text-xs font-bold font-default text-primary uppercase tracking-widest">
                  View Analysis
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-300" />
                </Link>
              </div>
            </CardContent>
          </Card>)}
      </div>
    </div>;
}
export default function FromTheWritingDesk() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="security-cameras-reviews" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="mb-12 md:mb-16 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 tracking-tight">
            Security & Camera Reviews
          </h2>
          <p className="text-muted-foreground font-sans text-lg max-w-2xl">
            Expert analysis and hands-on testing of the latest smart home surveillance systems to keep your property protected.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <BlogShowcaseConsumer />
        </PostsProvider>
      </div>
    </section>;
}